import { Button } from '@hotjar/hotsauce';
import { FullScreenHeader } from '@hotjar/insights-common/components/FullScreenView/FullScreenHeader/FullScreenHeader';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { RESPONSES, RESPONSES_DEFAULT } from '@hotjar/webapp/constants/routes/feedback';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentSite } from '@hotjar/site';
import { useTranslation } from 'react-i18next';
import { ConfirmLeaveModal } from '@hotjar/insights-common/';
import type { FeedbackWizardMode } from './FeedbackWizardHeader';

export interface FeedbackWizardFullScreenHeaderProps {
  isLoaded: boolean;
  widgetId: number | undefined;
  mode: FeedbackWizardMode;
  save: (navigateAfterSave: boolean) => Promise<void>;
  saveEnabled: boolean;
  highlightReviewSection: () => void;
  isSaving: boolean;
  isFormDirty: boolean;
}

export const FeedbackWizardFullScreenHeader = ({
  isLoaded,
  widgetId,
  mode,
  save,
  saveEnabled,
  highlightReviewSection,
  isSaving,
  isFormDirty,
}: FeedbackWizardFullScreenHeaderProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const navigate = useNavigate();
  const currentSite = useCurrentSite().data;
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const leaveWizard = useCallback(() => {
    if (currentSite === undefined) return;

    if (widgetId === undefined) {
      navigate(getRoute(RESPONSES_DEFAULT.path, { siteId: currentSite.id }));
    } else {
      navigate(getRoute(RESPONSES.path, { siteId: currentSite.id, feedbackWidgetId: widgetId }));
    }
    setOpenConfirmationModal(false);
  }, [widgetId, currentSite, navigate]);

  const onClose = useCallback(() => {
    if (isFormDirty) setOpenConfirmationModal(true);
    else leaveWizard();
  }, [isFormDirty, leaveWizard]);

  const onSave = useCallback(() => save(mode === 'create'), [save, mode]);

  return (
    <>
      <FullScreenHeader
        title={t(
          mode === 'create'
            ? 'wizard.header.new-feedback-widget'
            : 'wizard.header.edit-feedback-widget',
        )}
        onClose={onClose}
        contextActions={
          isLoaded && (
            <>
              {!saveEnabled && (
                <Button size="s" kind="secondary" onClick={highlightReviewSection}>
                  {t('wizard.header.action.review')}
                </Button>
              )}

              <Button
                size="s"
                onClick={onSave}
                isDisabled={!saveEnabled}
                isLoading={isSaving}
                loadingText={t('wizard.header.saving-message')}
              >
                {t(
                  mode === 'create'
                    ? 'wizard.header.action.create-feedback-widget'
                    : 'wizard.header.action.save-feedback',
                )}
              </Button>
            </>
          )
        }
      />
      <ConfirmLeaveModal
        isOpen={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        onConfirmLeave={leaveWizard}
      />
    </>
  );
};
